import { useState } from 'react'
import { CheckCircle, ChevronRight, Copy } from 'lucide-react'
import { toast } from 'sonner'
import type { AppliedCredit, CreditNote, Invoice } from '@/types/invoice'
import { InlineField } from '@/components/common'
import { formatCurrency, formatDate } from './invoiceUtils'

interface Props {
  invoice: Invoice
  credits: { credits: CreditNote[]; applied: AppliedCredit[] }
  onSave: (field: string, value: string | number | null) => Promise<unknown>
  onApplyCredits: (ids: number[]) => void
}

/** Selten Gebrauchtes: Positionen, Gutschriften, Notizen, Verlauf — zugeklappt. */
export default function InvoiceExtrasAccordion({ invoice, credits, onSave, onApplyCredits }: Props) {
  const [selectedCredits, setSelectedCredits] = useState<number[]>([])

  const isReminder = invoice.type === 'reminder'
  const available = credits.credits.filter(
    (c) => !credits.applied.some((a) => a.credit_note_id === c.id),
  )
  const showCredits = invoice.type === 'incoming' && (credits.applied.length > 0 || available.length > 0)

  // Mahnungs-Positionen tragen die Nummer der gemahnten Rechnung im Text — ist die
  // bezahlt, wird die Zeile durchgestrichen.
  const linkedStatus = new Map<string, string>()
  ;(invoice.linkedInvoices || []).forEach((li) => {
    if (li.invoice_number) linkedStatus.set(li.invoice_number, li.status)
  })
  const isLinkedPaid = (description: string | null) => {
    if (!isReminder || !description) return false
    const m = description.match(/\b(\d{7,10})\b/)
    return m ? linkedStatus.get(m[1]) === 'paid' : false
  }

  const appliedSum = credits.applied.reduce((s, a) => s + (a.applied_amount || 0), 0)
  const chosenSum = available
    .filter((c) => selectedCredits.includes(c.id))
    .reduce((s, c) => s + Math.abs(c.gross_amount || 0), 0)

  return (
    <>
      {invoice.lineItems?.length > 0 && (
        <Section title="Positionen" count={invoice.lineItems.length}>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400">
                  <th className="px-3 py-1.5 font-semibold">#</th>
                  <th className="px-3 py-1.5 font-semibold">Bezeichnung</th>
                  <th className="px-3 py-1.5 text-right font-semibold">Menge</th>
                  <th className="px-3 py-1.5 text-right font-semibold">EP</th>
                  <th className="px-3 py-1.5 text-right font-semibold">GP</th>
                </tr>
              </thead>
              <tbody>
                {invoice.lineItems.map((item, idx) => {
                  const paid = isLinkedPaid(item.description)
                  const strike = paid ? 'text-gray-400 line-through' : ''
                  return (
                    <tr key={item.id ?? idx} className="border-t border-gray-100">
                      <td className="px-3 py-1.5 text-right text-gray-400">
                        {paid ? <CheckCircle className="ml-auto h-3.5 w-3.5 text-emerald-500" /> : item.position_number}
                      </td>
                      <td className={`px-3 py-1.5 ${strike}`}>{item.description}</td>
                      <td className={`whitespace-nowrap px-3 py-1.5 text-right tabular-nums ${strike}`}>
                        {item.quantity} {item.unit}
                      </td>
                      <td className={`whitespace-nowrap px-3 py-1.5 text-right tabular-nums ${strike}`}>{formatCurrency(item.unit_price)}</td>
                      <td className={`whitespace-nowrap px-3 py-1.5 text-right font-medium tabular-nums ${strike}`}>{formatCurrency(item.total_price)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {showCredits && (
        <Section title="Gutschriften" count={credits.applied.length + available.length} defaultOpen>
          <div className="space-y-1.5 p-3">
            {credits.applied.map((a, idx) => {
              const upToHere = credits.applied.slice(0, idx + 1).reduce((s, x) => s + (x.applied_amount || 0), 0)
              const rest = (invoice.gross_amount || 0) - upToHere
              const reference = `${invoice.invoice_number || ''} / GS ${a.invoice_number}`
              return (
                <div key={a.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-green-200 bg-green-50 px-2.5 py-1.5 text-xs">
                  <CheckCircle className="h-3.5 w-3.5 flex-shrink-0 text-green-600" />
                  <span className="rounded bg-green-100 px-1.5 py-0.5 font-mono text-[11px] text-green-800">{reference}</span>
                  <CopyBtn text={reference} label="Verwendungszweck" />
                  <span className="ml-auto text-gray-500">Rest</span>
                  <span className="font-mono font-bold tabular-nums text-green-700">{formatCurrency(Math.max(0, rest))}</span>
                  <CopyBtn text={Math.max(0, rest).toFixed(2).replace('.', ',')} label="Restbetrag" />
                </div>
              )
            })}

            {available.map((c) => (
              <label key={c.id} className="flex cursor-pointer items-center gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs hover:bg-gray-50">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded"
                  checked={selectedCredits.includes(c.id)}
                  onChange={(e) => setSelectedCredits((prev) =>
                    e.target.checked ? [...prev, c.id] : prev.filter((id) => id !== c.id))}
                />
                <span className="font-mono">{c.invoice_number}</span>
                <span className="text-[10px] text-gray-500">{formatDate(c.invoice_date)}</span>
                <span className="ml-auto font-medium tabular-nums text-emerald-600">−{formatCurrency(Math.abs(c.gross_amount || 0))}</span>
              </label>
            ))}

            {selectedCredits.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 pt-2 text-xs">
                <span className="text-gray-600">
                  {selectedCredits.length} Gutschrift(en) = <strong className="text-emerald-600">−{formatCurrency(chosenSum)}</strong>
                  {' → '}zahlbar <strong className="tabular-nums">{formatCurrency((invoice.gross_amount || 0) - chosenSum - appliedSum)}</strong>
                </span>
                <button
                  type="button"
                  onClick={() => { onApplyCredits(selectedCredits); setSelectedCredits([]) }}
                  className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-primary-600"
                >
                  Gutschriften anrechnen
                </button>
              </div>
            )}
          </div>
        </Section>
      )}

      <Section title="Notizen" defaultOpen={!!invoice.notes}>
        <InlineField
          label="Notiz" field="notes" value={invoice.notes} type="textarea"
          placeholder="Klicken zum Erfassen…" onSave={onSave}
        />
      </Section>

      {invoice.history?.length > 0 && (
        <Section title="Verlauf" count={invoice.history.length}>
          <div className="space-y-1.5 p-3">
            {invoice.history.map((h) => (
              <div key={h.id} className="grid grid-cols-[76px_minmax(0,1fr)] gap-2 text-xs">
                <time className="pt-px text-[10px] tabular-nums text-gray-400">{formatDate(h.created_at)}</time>
                <p className="min-w-0 text-gray-600">
                  <b className="font-semibold text-gray-800">{h.user_name || 'System'}</b>
                  {' — '}{h.note || h.action}
                </p>
              </div>
            ))}
          </div>
        </Section>
      )}
    </>
  )
}

function Section({ title, count, defaultOpen, children }: {
  title: string
  count?: number
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  return (
    <details open={defaultOpen} className="group overflow-hidden rounded-lg border border-gray-200 bg-white">
      <summary className="flex cursor-pointer list-none items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400 transition-colors hover:text-gray-700 group-open:border-b group-open:border-gray-100">
        <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
        {title}
        {count != null && (
          <span className="rounded-full border border-gray-200 bg-gray-50 px-1.5 tracking-normal text-gray-500">{count}</span>
        )}
      </summary>
      {children}
    </details>
  )
}

function CopyBtn({ text, label }: { text: string; label: string }) {
  return (
    <button
      type="button"
      title={`${label} kopieren`}
      onClick={() => { navigator.clipboard?.writeText(text); toast.success(`${label} kopiert`) }}
      className="rounded p-0.5 text-gray-400 transition-colors hover:text-green-700"
    >
      <Copy className="h-3.5 w-3.5" />
    </button>
  )
}
